// src/helpers/qrErrorHandler.js

import Swal from 'sweetalert2';

/**
 * Helper para manejo de errores específicos del sistema QR
 */
export const qrErrorHandler = {
    /**
     * Maneja errores de escaneo QR
     */
    handleScanError: (error) => {
        console.error('QR Scan Error:', error);

        let title = 'Error de Escaneo';
        let message = 'Ha ocurrido un error al escanear el código QR';
        let icon = 'error';

        if (error.name === 'NotAllowedError') {
            title = 'Permisos de Cámara';
            message = 'Se necesitan permisos de cámara para escanear códigos QR. Por favor, permite el acceso a la cámara en la configuración del navegador.';
            icon = 'warning';
        } else if (error.name === 'NotFoundError') {
            title = 'Cámara No Encontrada';
            message = 'No se encontró ninguna cámara disponible. Verifica que tu dispositivo tenga una cámara conectada.';
            icon = 'warning';
        } else if (error.name === 'NotReadableError') {
            title = 'Cámara en Uso';
            message = 'La cámara está siendo utilizada por otra aplicación. Cierra otras aplicaciones que puedan estar usando la cámara.';
            icon = 'warning';
        } else if (error.message && error.message.includes('QR code parse error')) {
            title = 'QR Inválido';
            message = 'El código QR escaneado no es válido. Asegúrate de que sea un código QR de PetitPrime.';
            icon = 'warning';
        } else if (error.message && error.message.includes('No QR code found')) {
            // Este error es normal durante el escaneo, no mostramos alerta
            return;
        }

        return Swal.fire({
            title,
            text: message,
            icon,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3B82F6'
        });
    },

    /**
     * Maneja errores de validación de QR
     */
    handleValidationError: (error) => {
        console.error('QR Validation Error:', error);

        let title = 'QR Inválido';
        let message = 'El código QR no es válido';
        let icon = 'warning';

        if (error.includes('QR expirado')) {
            title = 'QR Expirado';
            message = 'Este código QR ha expirado. Pide al cliente que actualice su ticket digital.';
        } else if (error.includes('Estructura de QR inválida')) {
            title = 'QR Malformado';
            message = 'El código QR no tiene la estructura correcta. Asegúrate de que sea un ticket de PetitPrime.';
        } else if (error.includes('Tipo de QR no válido')) {
            title = 'Tipo de QR Incorrecto';
            message = 'Este código QR no es para acumulación de puntos. Verifica que sea el ticket correcto.';
        }

        return Swal.fire({
            title,
            text: message,
            icon,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3B82F6'
        });
    },

    /**
     * Maneja errores de conexión con el backend
     */
    handleBackendError: (error) => {
        console.error('Backend Error:', error);

        let title = 'Error de Conexión';
        let message = 'No se pudo conectar con el servidor';
        let icon = 'error';

        if (error.status === 404) {
            title = 'Ticket No Encontrado';
            message = 'No se pudo encontrar el ticket. Verifica que el código QR sea correcto.';
            icon = 'warning';
        } else if (error.status === 403) {
            title = 'Acceso Denegado';
            message = 'No tienes permisos para realizar esta acción.';
            icon = 'warning';
        } else if (error.status === 400) {
            title = 'Datos Inválidos';
            message = 'Los datos enviados no son válidos. Intenta nuevamente.';
            icon = 'warning';
        } else if (error.status >= 500) {
            title = 'Error del Servidor';
            message = 'Ha ocurrido un error en el servidor. Intenta nuevamente en unos minutos.';
            icon = 'error';
        } else if (error.message) {
            message = error.message;
        }

        return Swal.fire({
            title,
            text: message,
            icon,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3B82F6'
        });
    },

    /**
     * Maneja errores de acumulación de puntos
     */
    handleAccumulationError: (error) => {
        console.error('Accumulation Error:', error);

        let title = 'Error al Acumular Puntos';
        let message = 'No se pudieron acumular los puntos';
        let icon = 'error';

        if (error.message && error.message.includes('máximo de puntos')) {
            title = 'Límite de Puntos Alcanzado';
            message = 'El cliente ya ha alcanzado el máximo de puntos permitidos.';
            icon = 'warning';
        } else if (error.message && error.message.includes('ticket inactivo')) {
            title = 'Ticket Inactivo';
            message = 'El ticket del cliente no está activo.';
            icon = 'warning';
        } else if (error.message && error.message.includes('cliente inactivo')) {
            title = 'Cliente Inactivo';
            message = 'El cliente no está activo en el sistema.';
            icon = 'warning';
        } else if (error.message) {
            message = error.message;
        }

        return Swal.fire({
            title,
            text: message,
            icon,
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3B82F6'
        });
    },

    /**
     * Maneja errores generales
     */
    handleGeneralError: (error, context = '') => {
        console.error(`General Error ${context}:`, error);

        return Swal.fire({
            title: 'Error Inesperado',
            text: `Ha ocurrido un error inesperado${context ? ` en ${context}` : ''}. Intenta nuevamente.`,
            icon: 'error',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3B82F6'
        });
    },

    /**
     * Muestra mensaje de éxito
     */
    showSuccess: (title, message, details = null) => {
        return Swal.fire({
            title,
            html: `
                <div class="text-center">
                    <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                    </div>
                    <p class="text-lg font-semibold text-gray-800 mb-2">${title}</p>
                    <p class="text-gray-600">${message}</p>
                    ${details ? `<div class="mt-4 p-3 bg-green-50 rounded-lg text-sm text-green-800">${details}</div>` : ''}
                </div>
            `,
            icon: 'success',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#10B981'
        });
    },

    /**
     * Muestra mensaje de información
     */
    showInfo: (title, message) => {
        return Swal.fire({
            title,
            text: message,
            icon: 'info',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#3B82F6'
        });
    },

    /**
     * Muestra mensaje de advertencia
     */
    showWarning: (title, message) => {
        return Swal.fire({
            title,
            text: message,
            icon: 'warning',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#F59E0B'
        });
    }
};

export default qrErrorHandler;
